import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners";
import { PartnerType } from "@/app/utils/partners.enum";
import { CreditCard } from "@/view/components/CreditCard";
import masks, { applyMask } from "@/app/utils/masks";
import { Button } from "@/view/components/ui/button";
import { useSelectValues } from "./useSelectValues";
import { Slider } from "@/view/components/ui/slider";
import { Skeleton } from "@/view/components/ui/skeleton";
import { cn } from "@/app/utils/cn";
import type { FieldError, FieldErrors } from "react-hook-form";
import { useFormContext, useFormState } from "react-hook-form";
import { Input } from "../../CreditBalanceForm/Input";
import { Spinner } from "@/view/components/ui/spinner";
import { CreditPartner } from "../../../useCreditController";

function messageForCrefazAdicional(
  errors: FieldErrors,
  convenioDadosId: number,
): string | undefined {
  const crefaz = errors.crefaz as FieldErrors | undefined;
  const raw = crefaz?.adicionais as
    | (FieldError | undefined)[]
    | Record<number, FieldError | undefined>
    | undefined;
  if (raw == null) return undefined;
  const entry = Array.isArray(raw)
    ? raw[convenioDadosId]
    : raw[convenioDadosId];
  return entry?.message;
}

export const SelectValues = () => {
  const { type, setStep2mode } = useSelfHiringContext();

  const methods = useFormContext();
  const { errors } = useFormState({ control: methods.control });
  const {
    advanceToDocuments,
    offer,
    inputDigits,
    setInputDigits,
    handleSliderChange,
    selectedValue,
    installments,
    selectedInstallment,
    loadingInstallments,
    onSelectInstallment,
    isSendingData,
    isLoadingOffersData,
  } = useSelectValues(methods);

  const allowedValues = offer?.valores?.map((v) => v.valor) ?? [];
  const hasAllowedValues = allowedValues.length > 0;
  const minAllowed = hasAllowedValues ? Math.min(...allowedValues) : 0;
  const maxAllowed = hasAllowedValues ? Math.max(...allowedValues) : 0;

  const isProposalReady =
    !!selectedValue && !!selectedInstallment && !loadingInstallments;

  if (isLoadingOffersData) {
    return <Spinner />;
  }

  if (!offer) {
    return (
      <CreditCard className="max-w-lg gap-4">
        <div>
          <p className="text-center text-sm font-semibold md:text-base">
            Nenhuma oferta disponível.
          </p>
        </div>

        <Button
          type="button"
          onClick={() => setStep2mode("partner")}
          className="mt-2 h-9 w-44 font-semibold text-white"
        >
          Voltar
        </Button>
      </CreditCard>
    );
  }

  const isDataLeitura = (nome: string) => {
    return nome?.toLowerCase() === "data de leitura";
  };

  return (
    <>
      <div
        className="mb-4 flex h-14 w-full shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: getPartnerBgColor(type as PartnerType) }}
      >
        <img
          src={getPartnerImage(type as CreditPartner)}
          alt={(type as string) ?? ""}
          className={cn(
            "h-14 object-contain",
            type === PartnerType.PAN && "h-18 max-w-32",
          )}
        />
      </div>
      <CreditCard className="max-w-lg gap-4">
        <p className="text-center text-sm font-semibold md:text-base ">
          Para avançar, é <span className="text-primary">necessário</span>{" "}
          escolher os valores!
        </p>

        {offer && (
          <>
            <div className="rounded-xl border border-primary p-2 px-3 shadow-sm">
              <label className="text-xs font-semibold text-[#979797]">
                Selecione o valor que deseja sacar
              </label>
              <div className="mt-2 flex items-center">
                <input
                  type="text"
                  className="ml-2 w-full border-none bg-transparent text-center text-xl font-semibold text-black01 focus-visible:outline-none focus-visible:ring-0 md:text-2xl"
                  value={inputDigits ? masks.currency(Number(inputDigits)) : ""}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    setInputDigits(digits);
                  }}
                  onBlur={() => {
                    const value = Number(inputDigits) / 100;
                    const closestValue = allowedValues.reduce(
                      (prev, curr) =>
                        Math.abs(curr - value) < Math.abs(prev - value)
                          ? curr
                          : prev,
                      allowedValues[0] ?? 0,
                    );
                    handleSliderChange(closestValue);
                    setInputDigits((closestValue * 100).toString());
                  }}
                />
              </div>
            </div>

            {hasAllowedValues && (
              <div className="w-full">
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">
                    {masks.currency(minAllowed * 100)}
                  </span>
                  <div className="flex-1 px-4">
                    <Slider
                      min={minAllowed}
                      max={maxAllowed}
                      step={1}
                      value={selectedValue}
                      allowedValues={allowedValues}
                      onChange={(e) =>
                        handleSliderChange(Number(e.target.value))
                      }
                    />
                  </div>
                  <span className="text-xs font-semibold text-gray-700">
                    {masks.currency(maxAllowed * 100)}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        <div className="my-4 flex w-full flex-col gap-3">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              name="email"
              label="E-mail"
              placeholder="E-mail"
              maxLength={30}
              error={errors.email?.message as string}
            />
            <Input
              name="phone"
              label="Telefone"
              placeholder="Telefone"
              maxLength={15}
              onInput={applyMask(masks.phone)}
              error={errors.phone?.message as string}
            />
            {!!offer?.adicionais?.length &&
              offer.adicionais.map((adicional) => (
                <Input
                  key={adicional.convenioDadosId}
                  name={`crefaz.adicionais.${adicional.convenioDadosId}`}
                  label={adicional.nome}
                  placeholder={adicional.nome}
                  maxLength={isDataLeitura(adicional.nome) ? 10 : undefined}
                  onInput={
                    isDataLeitura(adicional.nome)
                      ? applyMask(masks.date)
                      : undefined
                  }
                  error={messageForCrefazAdicional(
                    errors,
                    adicional.convenioDadosId,
                  )}
                />
              ))}
          </div>
        </div>

        <div>
          <p className="font-medium leading-tight">
            <span className="font-bold">Excelente! </span>
            Você pode <br /> optar por até
            <span className="font-bold">
              {" "}
              {masks.currency(Math.round(selectedValue * 100))}
            </span>
          </p>
          <p className="pt-2 text-xs font-medium text-gray-500">
            Você optou por
            <span className="font-bold text-black">
              {" "}
              {masks.currency(Math.round(selectedValue * 100))}{" "}
            </span>
            - Veja as melhores opções
          </p>
        </div>

        <div className="grid w-full grid-cols-2 gap-3 md:grid-cols-3">
          {loadingInstallments ? (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className=" h-20  rounded-md" />
              ))}
            </>
          ) : (
            installments.map((option) => {
              const textColor = getPartnerBgColor(type as PartnerType);
              const isSelected = selectedInstallment?.prazo === option.prazo;
              return (
                <button
                  key={option.prazo}
                  type="button"
                  onClick={() => onSelectInstallment(option)}
                  className={cn(
                    "flex cursor-pointer flex-col items-start justify-between gap-1 rounded-xl border border-gray-300 bg-gray-50 p-3 transition-all",
                    isSelected && "border-[#fedf5a] bg-[#fefae8]",
                  )}
                >
                  <p style={{ color: textColor }} className="text-sm font-bold">
                    {option.prazo}x
                  </p>
                  <p className="text-md font-semibold">
                    {masks.currency(Math.round(option.valor * 100))}
                  </p>
                </button>
              );
            })
          )}
        </div>

        <Button
          type="button"
          isLoading={isSendingData}
          disabled={!isProposalReady || isSendingData}
          onClick={advanceToDocuments}
          className="mt-4 w-full font-semibold text-white"
        >
          Avançar
        </Button>
      </CreditCard>
    </>
  );
};
